/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { motion } from "framer-motion";
import { Search, MapPin, TrendingUp, ExternalLink } from "lucide-react";

const searchExamples = [
  { query: "Plombier Tarn", clicks: "+2 400 visites / mois" },
  { query: "Boulangerie Albi", clicks: "+1 800 visites / mois" },
  { query: "Restaurant Marssac-sur-Tarn", clicks: "+880 visites / mois" },
  { query: "Garage Castres", clicks: "+1 200 visites / mois" },
];

const points = [
  {
    title: "Pourquoi c'est essentiel",
    text: "8 clients sur 10 cherchent une entreprise sur Google avant de la contacter. Sans référencement, ils passent à côté de vous.",
  },
  {
    title: "Comment cela vous apporte des clients",
    text: "Apparaître en premier sur Google + Google Maps + avis + numéro visible = vos futurs clients vous appellent au bon moment.",
  },
  {
    title: "Sans référencement, un site sert à peu",
    text: "Un site magnifique mais invisible sur Google, c'est une boutique cachée au fond d'une rue déserte. Le référencement, c'est ce qui amène les visiteurs.",
  },
];

export const SEOExplainer = () => (
  <section id="seo" data-testid="seo-explainer-section" className="py-24 md:py-32 bg-white relative overflow-hidden">
    <div
      className="absolute top-0 right-0 w-[500px] h-[500px] -z-0 opacity-60 pointer-events-none"
      style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.10), transparent 70%)" }}
      aria-hidden
    />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <p className="font-mono-label text-cyan-600 flex items-center">
            <span className="rule bg-cyan" /> Être trouvé sur Google
          </p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
            Comment vos clients vous <span className="bg-gradient-to-r from-ink to-cyan-600 bg-clip-text text-transparent">trouvent sur Google</span> ?
          </h2>
          <p className="mt-8 text-ink/75 text-lg leading-relaxed">
            On appelle ça le <em>référencement</em>. C'est simple : c'est
            l'ensemble des techniques qui font{" "}
            <strong className="text-ink">apparaître votre entreprise sur Google</strong> quand
            un client cherche vos services.
          </p>

          {/* Mock search bar */}
          <div className="mt-10 bg-paper-soft border border-ink/10 p-5 lift">
            <div className="flex items-center gap-3 bg-white border border-ink/15 px-4 h-12">
              <Search size={16} className="text-ink/40" />
              <span className="text-ink/70 text-sm">plombier Tarn</span>
              <span className="ml-auto h-2 w-2 rounded-full bg-cyan animate-pulse" />
            </div>
            <p className="mt-3 text-xs text-ink/55">→ Votre entreprise apparaît en tête des résultats locaux Google Maps.</p>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-8">
          {points.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-paper-soft border border-ink/10 p-8 relative lift"
              data-testid={`seo-point-${i}`}
            >
              <span className="absolute -left-4 top-8 hidden md:flex h-8 w-8 items-center justify-center bg-gradient-to-br from-ink to-cyan-600 text-white text-xs font-mono-label">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-2xl text-ink">{p.title}</h3>
              <p className="mt-3 text-ink/70 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}

          {/* Local search examples */}
          <div className="bg-gradient-to-br from-ink via-ink to-ink-700 text-white p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-60 h-60 bg-cyan/40 rounded-full blur-3xl" aria-hidden />
            <p className="font-mono-label text-white/65 relative">Exemples concrets dans le Tarn</p>
            <h3 className="mt-3 font-display text-2xl md:text-3xl relative">Recherches Google où vous pouvez apparaître</h3>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 relative">
              {searchExamples.map((s) => (
                <li key={s.query} className="flex items-center justify-between bg-white/5 backdrop-blur px-4 py-3 border border-white/10">
                  <span className="flex items-center gap-2 text-sm">
                    <MapPin size={14} className="text-cyan-400" /> {s.query}
                  </span>
                  <span className="text-xs text-cyan-300 font-medium flex items-center gap-1">
                    <TrendingUp size={12} /> {s.clicks}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-white/55 relative flex items-center gap-1.5">
              <ExternalLink size={11} />
              Source : Google Keyword Planner & Semrush — volumes mensuels estimés région Tarn.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
